import * as D3Array from 'd3-array';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useResizeObserver } from '../../../utils/useResizeObserver';
import { assembleChartTitle, calcInfoValues, calcYDomain, dataProcessingPipeline, datePresets, hasBenchmarkData, parseData } from '../helpers';
import type { DataProps, InfoValues, Margins, ParsedDataProps, XDomain, XRange, YDomain } from '../types';
import { DatePresets } from '../types';

export interface UsePerformanceDataParams {
  data: any;
  refetch: any;
  reportCurrency: string;
  currentLanguage: string;
  margins: Margins;
  dataHeader: string;
}

export interface UsePerformanceDataReturn {
  preparedData: ParsedDataProps[];
  parsedData: ParsedDataProps[];
  preparedDetailData: ParsedDataProps[];
  xDomain: XDomain;
  detailXDomain: XDomain | null;
  yDomain: YDomain;
  detailYDomain: YDomain | null;
  dataLoaded: boolean;
  performanceInfo: InfoValues;
  title: string;
  xRange: XRange;
  hasBenchmark: boolean;
  fetchFailure: boolean;
  width: number;
  height: number;
  resizeRef: (node: HTMLElement | null) => void;
  setDataLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchFailure: React.Dispatch<React.SetStateAction<boolean>>;
  setPreparedDetailData: React.Dispatch<React.SetStateAction<ParsedDataProps[]>>;
  setDetailXDomain: React.Dispatch<React.SetStateAction<XDomain | null>>;
  setDetailYDomain: React.Dispatch<React.SetStateAction<YDomain | null>>;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setPerformanceInfo: React.Dispatch<React.SetStateAction<InfoValues>>;
}

export function usePerformanceData({ data, refetch, reportCurrency, currentLanguage, margins, dataHeader }: UsePerformanceDataParams): UsePerformanceDataReturn {
  // Data state
  const [preparedData, setPreparedData] = useState<ParsedDataProps[]>([]);
  const [parsedData, setParsedData] = useState<ParsedDataProps[]>([]);
  const [preparedDetailData, setPreparedDetailData] = useState<ParsedDataProps[]>([]);
  const [xDomain, setXDomain] = useState<XDomain>([new Date(), new Date()]);
  const [detailXDomain, setDetailXDomain] = useState<XDomain | null>(null);
  const [yDomain, setYDomain] = useState<YDomain>([0, 0]);
  const [detailYDomain, setDetailYDomain] = useState<YDomain | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [performanceInfo, setPerformanceInfo] = useState<InfoValues>({
    performance: null,
    performancePA: null,
    benchmark: null,
    benchmarkPA: null,
  });
  const [title, setTitle] = useState(dataHeader || '');
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [xRange, setXRange] = useState<XRange>([0, 0]);
  const [hasBenchmark, setHasBenchmark] = useState(false);
  const [fetchFailure, setFetchFailure] = useState(false);

  // Refs for tracking previous values
  const prevReportCurrencyRef = useRef(reportCurrency);
  const prevChartRef = useRef(data.chart);

  // Observe container dimensions using native ResizeObserver
  const { ref: resizeRef, width: observedWidth, height: observedHeight } = useResizeObserver();

  // componentDidMount - initial refetch
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch is stable
  useEffect(() => {
    refetch(
      {
        reportcurrency: reportCurrency,
        loadData: true,
      },
      { fetchPolicy: 'network-only' },
    );
  }, []);

  // componentDidUpdate - handle reportCurrency changes
  useEffect(() => {
    if (prevReportCurrencyRef.current !== reportCurrency) {
      refetch(
        {
          reportcurrency: reportCurrency,
          loadData: true,
        },
        { fetchPolicy: 'network-only' },
      );
    }
    prevReportCurrencyRef.current = reportCurrency;
  }, [reportCurrency, refetch]);

  // componentDidUpdate - handle chart data arrival
  useEffect(() => {
    const processChartData = async () => {
      if (!prevChartRef.current && data.chart && xRange[1] > 0) {
        const newParsedData = parseData(data.chart.data as DataProps[]);
        const newXDomain = D3Array.extent<ParsedDataProps, Date>(newParsedData, (d) => d.date);
        const newHasBenchmark = hasBenchmarkData(newParsedData);

        const { processedData } = await dataProcessingPipeline(newParsedData, newXDomain, xRange, newHasBenchmark);
        const newYDomain = calcYDomain(processedData, newHasBenchmark);

        let newDetailXDomain = detailXDomain;
        if (!detailXDomain) {
          newDetailXDomain = [datePresets(newXDomain)[DatePresets.TenYears], newXDomain[1]] as XDomain;
        }

        const { processedData: processedDetailData, verifiedXDomain: verifiedDetailXDomain } = await dataProcessingPipeline(newParsedData, newDetailXDomain, xRange, newHasBenchmark);
        const newDetailYDomain = calcYDomain(processedDetailData, newHasBenchmark);

        setDataLoaded(true);
        setHasBenchmark(newHasBenchmark);
        setXDomain(newXDomain);
        setYDomain(newYDomain);
        setParsedData(newParsedData);
        setPreparedData(processedData);
        setPreparedDetailData(processedDetailData);
        setDetailYDomain(newDetailYDomain);
        setDetailXDomain(verifiedDetailXDomain);
        setTitle(assembleChartTitle(reportCurrency, dataHeader, verifiedDetailXDomain, currentLanguage));
        setPerformanceInfo(calcInfoValues(processedDetailData, verifiedDetailXDomain));

        prevChartRef.current = data.chart;
      }
    };

    processChartData();
  }, [data.chart, dataHeader, currentLanguage, reportCurrency, xRange, detailXDomain]);

  // onResize handler
  const onResize = useCallback(
    async (newWidth: number, newHeight: number) => {
      if (newWidth !== width && newWidth && newHeight) {
        const newXRange = [0, newWidth - margins.left - margins.right] as XRange;

        if (!data.chart || !parsedData || parsedData.length === 0 || !detailXDomain) {
          setWidth(newWidth);
          setHeight(newHeight);
          setXRange(newXRange);
          return;
        }

        const { processedData } = await dataProcessingPipeline(parsedData, xDomain, newXRange, hasBenchmark);
        const { processedData: processedDetailData, verifiedXDomain } = await dataProcessingPipeline(parsedData, detailXDomain, newXRange, hasBenchmark);

        setWidth(newWidth);
        setHeight(newHeight);
        setXRange(newXRange);
        setPreparedData(processedData);
        setPreparedDetailData(processedDetailData);
        setDetailXDomain(verifiedXDomain);
      }
    },
    [width, margins, data.chart, parsedData, xDomain, detailXDomain, hasBenchmark],
  );

  // Trigger onResize when observed container dimensions change
  useEffect(() => {
    if (observedWidth && observedHeight) {
      onResize(observedWidth, observedHeight);
    }
  }, [observedWidth, observedHeight, onResize]);

  return {
    preparedData,
    parsedData,
    preparedDetailData,
    xDomain,
    detailXDomain,
    yDomain,
    detailYDomain,
    dataLoaded,
    performanceInfo,
    title,
    xRange,
    hasBenchmark,
    fetchFailure,
    width,
    height,
    resizeRef,
    setDataLoaded,
    setFetchFailure,
    setPreparedDetailData,
    setDetailXDomain,
    setDetailYDomain,
    setTitle,
    setPerformanceInfo,
  };
}
